// src/components/NegativePromoHero.jsx
import React from "react";
import { Badge, Tooltip, Accordion } from "flowbite-react";
import { motion } from "framer-motion";
import {
  HiCheckCircle,
  HiLightningBolt,
  HiShieldCheck,
  HiChartBar,
  HiStar,
  HiPhone,
  HiArrowRight,
} from "react-icons/hi";

const FeatureCard = ({ icon, title, desc }) => (
  <motion.div
    whileHover={{ y: -4, scale: 1.01 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-5 shadow-sm overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-red-50/40 via-transparent to-white/0 pointer-events-none" />
    <div className="flex items-start gap-3">
      <div className="shrink-0 mt-0.5 text-blue-800">{icon}</div>
      <div>
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{desc}</div>
      </div>
    </div>
  </motion.div>
);

export default function NegativePromoHero({
  isSubscribed = false,
  priceLabel = "A partir de R$ 1,29 por CPF/CNPJ",
  onHire = () => {},
  onContact = () => {},
  learnMoreHref = "/docs/negativacao",
}) {
  return (
    <section className="relative w-full bg-white rounded-lg shadow-md">
      {/* HERO */}
      <div className="mx-auto py-5 px-5">
        <div className="flex flex-col items-start gap-6 md:gap-8">
          <div className="flex items-center gap-3">
            <Badge color={isSubscribed ? "success" : "purple"} size="sm">
              {isSubscribed
                ? "Ativo no seu plano"
                : "Novo • Negative seus clientes"}
            </Badge>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="text-3xl font-extrabold tracking-tight text-gray-900"
          >
            Reduza a inadimplência com a nossa{" "}
            <span className="bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">
              Negativação
            </span>
          </motion.h1>

          <p className="max-w-6xl text-gray-700 md:text-lg">
            Recupere seus recebimentos de forma simples e segura: automatize a
            negativação de devedores, envie notificações e acompanhe tudo em um
            só lugar.
          </p>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
            <div className="rounded-2xl border shadow-md p-4">
              <div className="text-xs uppercase text-gray-500">Custo</div>
              <div className="text-xl font-semibold">{priceLabel}</div>
              <div className="text-xs text-gray-500">Cobrança por envio</div>
            </div>
          </div>
        </div>

        {/* GRID DE BENEFÍCIOS */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <FeatureCard
            icon={<HiCheckCircle className="w-6 h-6" />}
            title="Recupere mais, mais rápido"
            desc="A pressão formal e rastreável da negativação acelera acordos e reduz reincidência."
          />
          <FeatureCard
            icon={<HiChartBar className="w-6 h-6" />}
            title="Dados e relatórios"
            desc="Painéis de conversão, tempo médio até acordo, remoções automáticas e auditoria completa."
          />
          <FeatureCard
            icon={<HiShieldCheck className="w-6 h-6" />}
            title="Compliance e segurança"
            desc="Fluxo aderente à legislação, com trilha de auditoria, consentimentos e logs por usuário."
          />
        </div>

        {/* FAQ / COMO FUNCIONA */}
        <div className="mt-8 grid grid-cols-1 gap-4">
          <div className="rounded-2xl border p-5 shadow-md bg-gray-50">
            <Accordion className="bg-white" collapseAll>
              <Accordion.Panel>
                <Accordion.Title>Como é feita a negativação?</Accordion.Title>
                <Accordion.Content>
                  <p className="text-sm text-gray-700">
                    A negativação é feita de forma simples e segura. A empresa
                    informa os dados do devedor e o valor, e o registro é
                    enviado para análise e inclusão.
                  </p>
                </Accordion.Content>
              </Accordion.Panel>
              <Accordion.Panel>
                <Accordion.Title>
                  É possível remover após acordo?
                </Accordion.Title>
                <Accordion.Content>
                  <p className="text-sm text-gray-700">
                    Sim — a baixa é realizada pela própria empresa que efetuou a
                    negativação. Após o acordo ou pagamento, basta selecionar o
                    motivo e confirmar a baixa diretamente pelo sistema.
                  </p>
                </Accordion.Content>
              </Accordion.Panel>
              <Accordion.Panel>
                <Accordion.Title>Como fica a auditoria?</Accordion.Title>
                <Accordion.Content>
                  <p className="text-sm text-gray-700">
                    Logs por usuário, data/hora, IP e trilha completa de
                    eventos.
                  </p>
                </Accordion.Content>
              </Accordion.Panel>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
